import { createContext, useCallback, useContext, useRef, useState, ReactNode } from 'react'
import { TOAST_DURATION_MS } from '../../shared/constants'

// 'warn' is the third kind, added 2026-09-08: a REFUSAL was being shown in the green of a
// success ("Bitte zuerst speichern…" when a pane switch is blocked). Green means "it worked".
// Named `warn` and not `info` — the colour already exists in the house as `.notice.warn`
// (amber on --warn), and two names for one meaning is how vocabularies drift apart.
interface Toast { id: number, message: string, type: 'ok' | 'err' | 'warn' }

interface ToastContextValue {
    toast: (message: string, type?: Toast['type']) => void
}

const ToastContext = createContext<ToastContextValue>({ toast: () => {} })

export function useToast() {
    return useContext(ToastContext)
}

type ToastType = Toast['type']

// Only 'ok' closes by itself. 'warn' and 'err' stay until clicked away — Betreiber, 28.10.2026:
// „Ist aber zu kurz zum lesen. Besser wäre es, wenn man den Toast weg klicken muss. Es wäre
// generell gut, wenn Warnungen und Fehler immer stehen bleiben." A warning is often the only
// trace that something was saved differently than meant; it must not vanish unread.
// null = no timer.
export function toastDismissMs(type: ToastType): number | null {
    return type === 'ok' ? TOAST_DURATION_MS : null
}

// Notifications, top-right. Success closes after a few seconds, warnings and errors stay;
// click or × closes any of them.
export function ToastProvider({ children }: { children: ReactNode }) {
    const [toasts, setToasts] = useState<Toast[]>([])
    const idRef = useRef(0)

    const remove = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), [])

    const toast = useCallback((message: string, type: ToastType = 'ok') => {
        const id = ++idRef.current
        setToasts((t) => [...t, { id, message, type }])
        // 5 s for 'ok', nicht 3,5 — Betreiber, 12.09.2026: „jetzt kann man ihn so schnell gar
        // nicht lesen bevor er weg ist." Seit 28.10.2026 gilt die Dauer nur noch fuer 'ok';
        // 'warn' und 'err' haben keine (siehe toastDismissMs). Weiter EINE Zahl, keine Staffelung.
        const ms = toastDismissMs(type)
        if (ms !== null) setTimeout(() => remove(id), ms)
    }, [remove])

    return (
        <ToastContext.Provider value={{ toast }}>
            {children}
            <div className="toast-container">
                {toasts.map((t) => (
                    <div key={t.id} className={`toast ${t.type}`} onClick={() => remove(t.id)}>
                        <span>{t.message}</span>
                        <button className="toast-close" onClick={(e) => { e.stopPropagation(); remove(t.id) }}>×</button>
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    )
}
